import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState, useCallback } from "react"
import { apiClient } from "@/lib/api-client"
import { toast } from "sonner"
import { uploadCv } from "@/modules/recruitment/applications/applicants-service"
import { useApplicationHub } from "@/modules/recruitment/applications/hooks/useApplicationHub"
import {
  PendingCvUpload,
  CvProcessedDto,
  cvStatusFromCode,
} from "@/modules/recruitment/candidates/dtos/cv-upload"
import { useCvUploadContext } from "@/modules/recruitment/context/CvUploadContext"
import {
  INSUFFICIENT_FUNDS_ERROR_CODE,
  showInsufficientFundsToast,
} from "@/lib/insufficient-funds"

export interface UploadCvResponse {
  id: string
  name: string
  email: string
  phone?: string
  location?: string
  skills?: string[]
  experienceYears?: number
  cvExtractionId?: string
}

export function useUploadCv(jobId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append("cvFile", file)
      formData.append("folderId", jobId)

      const response = await apiClient.post<UploadCvResponse>(
        "/api/candidate/upload-cv",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      )
      return response.data
    },
    onSuccess: (candidateData) => {
      queryClient.invalidateQueries({ queryKey: ["jobApplications", jobId] })
      queryClient.invalidateQueries({ queryKey: ["candidates"] })
      return candidateData
    },
  })
}

export function useCreateApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { jobId: string; candidateId: string }) => {
      const response = await apiClient.post("/api/application", data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["jobApplications", variables.jobId],
      })
      queryClient.invalidateQueries({ queryKey: ["applications"] })
    },
  })
}

const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB

export interface UseCvUploadOptions {
  organizationId: string
  vacancyId: string
  onCompleted?: (result: {
    candidateId?: string
    applicationId?: string
  }) => void
}

export function useCvUpload({
  organizationId,
  vacancyId,
  onCompleted,
}: UseCvUploadOptions) {
  const { onCvProcessed } = useApplicationHub()
  const ctx = useCvUploadContext()
  const [uploads, setUploads] = useState<PendingCvUpload[]>([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  const upload = useCallback(
    async (files: File[]) => {
      const validFiles = files.filter((file) => {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(`"${file.name}" is too large. Maximum size is 10 MB.`)
          return false
        }
        if (!ALLOWED_TYPES.includes(file.type)) {
          toast.error(`"${file.name}" is invalid. Only PDF, DOC, DOCX accepted.`)
          return false
        }
        return true
      })

      if (validFiles.length === 0) return

      setIsUploading(true)
      setUploadProgress(0)

      // Register a pending entry + SignalR listener for each file
      const tempIds = validFiles.map((file) => {
        const tempId = crypto.randomUUID()
        ctx.addUpload({ correlationId: tempId, fileName: file.name, status: "Pending", progress: 0 })
        return tempId
      })

      const correlationIds: string[] = []

      validFiles.forEach((file, i) => {
        const correlationIdRef = { current: "" }
        correlationIds.push(correlationIdRef.current)

        const unsubscribe = onCvProcessed((event: CvProcessedDto) => {
          if (!correlationIdRef.current || event.correlationId !== correlationIdRef.current) return
          unsubscribe()

          const patch = {
            status: cvStatusFromCode(event.status),
            candidateId: event.candidateId ?? undefined,
            applicationId: event.applicationId ?? undefined,
            errorMessage: event.errorMessage ?? undefined,
            progress: 100,
          }

          setUploads((prev) =>
            prev.map((u) => u.correlationId === event.correlationId ? { ...u, ...patch } : u),
          )
          ctx.updateUpload(event.correlationId, patch)

          if (event.status === 2) {
            toast.success(`"${file.name}" processed successfully`)
            onCompleted?.({ candidateId: event.candidateId ?? undefined, applicationId: event.applicationId ?? undefined })
          } else if (event.status === 3) {
            if (event.errorCode === INSUFFICIENT_FUNDS_ERROR_CODE) {
              showInsufficientFundsToast(event.errorMessage)
            } else {
              toast.error(event.errorMessage ?? `Failed to process "${file.name}"`)
            }
          }
        })

        // Store unsubscribe ref accessible after upload resolves
        correlationIds[i] = tempIds[i]
        ;(correlationIdRef as { current: string }).current = ""

        // Attach the ref to be filled after the HTTP call
        validFiles[i] = Object.assign(file, { _correlationRef: correlationIdRef })
      })

      try {
        const handleProgress = (percent: number) => {
          setUploadProgress(percent)
          tempIds.forEach((id) => ctx.updateUpload(id, { progress: percent }))
        }

        const accepted = await uploadCv(organizationId, vacancyId, validFiles, handleProgress)

        // accepted is an array — one entry per file
        const results = Array.isArray(accepted) ? accepted : [accepted]
        results.forEach((result, i) => {
          const tempId = tempIds[i]
          const file = validFiles[i] as File & { _correlationRef?: { current: string } }
          if (file._correlationRef) file._correlationRef.current = result.correlationId
          ctx.updateUploadId(tempId, result.correlationId)
          ctx.updateUpload(result.correlationId, { progress: 100, status: "Processing" })
        })

        toast.info(`${validFiles.length} CV${validFiles.length > 1 ? "s" : ""} uploaded — processing in background`)
      } catch {
        toast.error("Failed to upload CV(s)")
        tempIds.forEach((id) => ctx.updateUpload(id, { status: "Failed", errorMessage: "Upload failed" }))
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    },
    [organizationId, vacancyId, onCompleted, onCvProcessed, ctx],
  )

  return { upload, uploads, uploadProgress, isUploading }
}
